'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { ActivityCard } from '@/components/ActivityCard';
import { AtStakeCard } from '@/components/AtStakeCard';
import { BalanceCard } from '@/components/BalanceCard';
import { CommunityCompare } from '@/components/CommunityCompare';
import { HeroCard } from '@/components/HeroCard';
import { LeaderboardMini } from '@/components/LeaderboardMini';
import { MyBets } from '@/components/MyBets';
import { MyDisputes } from '@/components/MyDisputes';
import { PerformanceCard } from '@/components/PerformanceCard';
import { ProfileSkeleton } from '@/components/Skeleton';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/lib/use-pull-to-refresh';
import { TonConnectButton } from '@/components/TonConnectClient';
import { Transactions } from '@/components/Transactions';
import { UpcomingResolutions } from '@/components/UpcomingResolutions';
import { createApiClient, type MeResponse } from '@/lib/api';
import { getInitData, hapticImpact, isInTelegram } from '@/lib/tma';

// DepositSheet / WithdrawSheet — heavy modals, нужны только при клике.
// Lazy-load уменьшает /profile First Load JS на ~30-50 kB. ssr:false ОК
// потому что модалки рендерятся ТОЛЬКО после user-interaction.
const DepositSheet = dynamic(
  () => import('@/components/DepositSheet').then((m) => ({ default: m.DepositSheet })),
  { ssr: false },
);
const WithdrawSheet = dynamic(
  () => import('@/components/WithdrawSheet').then((m) => ({ default: m.WithdrawSheet })),
  { ssr: false },
);

export default function ProfilePage() {
  const [me, setMe] = useState<MeResponse['user'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [operatorRole, setOperatorRole] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const api = createApiClient(getInitData);

  function refresh() {
    api
      .me()
      .then((r) => setMe(r.user))
      .catch((err) => setError(err?.message ?? 'Не удалось загрузить профиль'));
    setRefreshKey((k) => k + 1);
  }

  // Async-обёртка для usePullToRefresh — должна resolves когда данные пришли,
  // иначе spinner indicator'а исчезнет преждевременно.
  async function refreshAsync() {
    try {
      const r = await api.me();
      setMe(r.user);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message ?? 'Не удалось загрузить профиль');
    }
  }
  const pull = usePullToRefresh(refreshAsync);

  useEffect(() => {
    if (!isInTelegram() && !new URL(window.location.href).searchParams.get('initData')) {
      setError('Открой Mini App через @Forecast_appbot — для авторизации нужен Telegram.');
      return;
    }
    refresh();
    api
      .adminMe()
      .then((r) => {
        setIsOperator(Boolean(r.operator));
        setOperatorRole(r.operator?.role ?? null);
      })
      .catch(() => {
        setIsOperator(false);
        setOperatorRole(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="px-4 pt-6">
        <h1 className="text-2xl font-bold mb-4">Профиль</h1>
        <div className="bg-bg-elev border border-line p-4 text-no text-sm">⚠️ {error}</div>
      </div>
    );
  }

  if (!me) return <ProfileSkeleton />;

  return (
    <div className="px-4 pt-6 pb-6">
      <PullIndicator distance={pull.pullDistance} active={pull.isRefreshing} />
      <HeroCard user={me} isOperator={isOperator} operatorRole={operatorRole} />

      <BalanceCard
        user={me}
        refreshKey={refreshKey}
        onDeposit={() => setShowDeposit(true)}
        onWithdraw={() => setShowWithdraw(true)}
      />

      {showDeposit && (
        <DepositSheet
          onClose={() => setShowDeposit(false)}
          onSuccess={refresh}
        />
      )}
      {showWithdraw && (
        <WithdrawSheet
          balanceUsdtMicro={me.balances.usdt_micro}
          onClose={() => setShowWithdraw(false)}
          onSuccess={refresh}
        />
      )}

      {isOperator && (
        <Link
          href="/admin/"
          onClick={() => hapticImpact('light')}
          className="bg-bg-elev border border-line p-4 mb-4 flex items-center justify-between transition-colors hover:border-white/20"
        >
          <div>
            <p className="font-semibold text-sm">⚙️ Admin dashboard</p>
            <p className="text-xs text-text-tertiary">Резолюция, withdrawals, аудит</p>
          </div>
          <span className="text-text-secondary">→</span>
        </Link>
      )}

      <AtStakeCard refreshKey={refreshKey} />

      <UpcomingResolutions refreshKey={refreshKey} />

      <MyBets refreshKey={refreshKey} />

      <PerformanceCard user={me} refreshKey={refreshKey} />

      <ActivityCard refreshKey={refreshKey} />

      <CommunityCompare refreshKey={refreshKey} />

      <LeaderboardMini refreshKey={refreshKey} />

      <MyDisputes refreshKey={refreshKey} />

      {/* TON Connect — connected wallet используется для withdraw без копипаста */}
      <div className="mb-4">
        <h2 className="display text-[14px] tracking-[0.05em] mb-2">TON КОШЕЛЁК</h2>
        <TonConnectButton />
      </div>

      <Transactions refreshKey={refreshKey} />
    </div>
  );
}
